/**
 * STEPS — static definition of every in-app onboarding step.
 *
 * Fields
 * -------
 * id          string   Matches the key the API uses for completion tracking.
 * stepNumber  number   Display position (1-based); re-calculated after filtering.
 * title       string   Large heading shown in the expanded card.
 * description string   Body text shown below the heading.
 * ctaLabel    string   Primary button text.
 * ctaKey      string   Key used by AppOnboardingWrapper to resolve the handler.
 * mediaType   'image' | 'video'
 * mediaSrc    string   Asset path (relative to src/assets/).
 * hideWhen    fn       Predicate (org) => boolean — return true to hide the step.
 */

/**
 * Translates "Job" to "Task" if the organization type is "operation".
 * @param {object} org - The selectedOrganization object.
 * @param {string} text - The text to translate.
 * @returns {string}
 */
export const translateJob = (org, text) => {
  if (!text) return "";
  const isOperation = org?.type === "operation";
  if (!isOperation) return text;
  // Replace "Job" with "Task" and "job" with "task"
  return text.replace(/Job/g, "Task").replace(/job/g, "task");
};

export const STEPS = [
  {
    id: "add_employees",
    stepNumber: 1,
    title: "Add Employees",
    description:
      "Invite your technicians so they can receive jobs and update progress from the mobile app.",
    ctaLabel: "Invite Members",
    ctaKey: "inviteMembers",
    mediaType: "image",
    mediaSrc: null, // replace with actual asset path when available
    hideWhen: () => false,
  },
  {
    id: "add_team",
    stepNumber: 2,
    title: "Add Team",
    description:
      "Create teams to organize your technicians and manage work assignments efficiently.",
    ctaLabel: "Add Team",
    ctaKey: "addTeam",
    mediaType: "image",
    mediaSrc: null,
    // Only visible for operation org type
    hideWhen: org => org?.type !== "operation",
  },
  {
    id: "add_customer",
    stepNumber: 2,
    title: "Add First Customer",
    description:
      "Create a customer profile to attach jobs, invoices, and history in one place.",
    ctaLabel: "Add Customer",
    ctaKey: "addCustomer",
    mediaType: "image",
    mediaSrc: null,
    // Hidden when org type is "operation" — customer concept maps to clients in service
    hideWhen: org => org?.type === "operation",
  },
  {
    id: "create_job",
    stepNumber: 3,
    title: "Create First Job",
    description: "Jobs represent service work assigned to technicians in the field.",
    ctaLabel: "Create Job",
    ctaKey: "createJob",
    mediaType: "image",
    mediaSrc: null,
    hideWhen: () => false,
  },
  {
    id: "create_schedule",
    stepNumber: 4,
    title: "Schedule Job",
    description: "Assign a date, time, and team member so everyone knows when and where to be.",
    ctaLabel: "Schedule Job",
    ctaKey: "scheduleJob",
    mediaType: "image",
    mediaSrc: null,
    hideWhen: () => false,
  },
  {
    id: "install_mobile_app",
    stepNumber: 5,
    title: "Install Mobile App",
    description:
      "Download the FieldServicely app so your team can update job status from the field.",
    ctaLabel: "Get the App",
    ctaKey: "installApp",
    mediaType: "image",
    mediaSrc: null,
    hideWhen: () => false,
  },
  {
    id: "complete_job",
    stepNumber: 6,
    title: "Complete First Job",
    description: "Mark a job as done and capture any notes, photos, or signatures needed.",
    ctaLabel: "Complete Job",
    ctaKey: "completeJob",
    mediaType: "image",
    mediaSrc: null,
    hideWhen: () => false,
  },
  {
    id: "create_invoice",
    stepNumber: 7,
    title: "Send Invoice",
    description:
      "Generate and send a professional invoice to your customer directly from the app.",
    ctaLabel: "Send Invoice",
    ctaKey: "sendInvoice",
    mediaType: "image",
    mediaSrc: null,
    hideWhen: org => org?.type === "operation",
  },
];

/**
 * Returns the subset of STEPS that are visible for the given organization,
 * with stepNumber re-indexed to reflect the filtered order.
 *
 * @param {object|null} org - The selectedOrganization object from Redux.
 * @returns {Array}
 */
export const getFilteredSteps = org => {
  const isOperation = org?.type === "operation";
  return STEPS.filter(step => !step.hideWhen(org)).map((step, index) => {
    let ctaLabel = translateJob(org, step.ctaLabel);

    // Special case for complete_job step CTA as requested: "View Tasks" or "View Jobs"
    if (step.id === "complete_job") {
      ctaLabel = isOperation ? "View Tasks" : "View Jobs";
    }

    return {
      ...step,
      stepNumber: index + 1,
      title: translateJob(org, step.title),
      description: translateJob(org, step.description),
      ctaLabel,
    };
  });
};
